'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Define the shape of our database context
export interface DatabaseContextType {
  isInitialized: boolean;
  isChecking: boolean;
  error: string | null;
  retryInitialization: () => Promise<void>;
}

// Create the context with default values
const DatabaseContext = createContext<DatabaseContextType>({
  isInitialized: false,
  isChecking: true,
  error: null,
  retryInitialization: async () => {},
});

// Custom hook to use the database context
export const useDatabaseContext = () => useContext(DatabaseContext);

// Function to test the schema
const testSchema = async (): Promise<{
  needsMigration: boolean;
  tableExists: boolean;
  success: boolean;
}> => {
  try {
    const response = await fetch('/api/schema/test');
    const data = await response.json();
    
    return {
      needsMigration: data.needsMigration || false,
      tableExists: data.tableExists || false,
      success: data.success || false
    };
  } catch (error) {
    console.error('Error testing schema:', error);
    return {
      needsMigration: true,
      tableExists: false,
      success: false
    };
  }
};

// Function to run migrations
const runMigrations = async (): Promise<boolean> => {
  try {
    const response = await fetch('/api/schema/migrate');
    const data = await response.json();
    
    return data.success || false;
  } catch (error) {
    console.error('Error running migrations:', error);
    return false;
  }
};

// Provider component
export const DatabaseProvider = ({ children }: { children: ReactNode }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Initialize the database
  const initializeDatabase = async () => {
    setIsChecking(true);
    setError(null);
    
    try {
      const schemaStatus = await testSchema();
      
      if (schemaStatus.tableExists) {
        // Schema already exists
        setIsInitialized(true);
        setIsChecking(false);
        return;
      }
      
      if (schemaStatus.needsMigration) {
        // Need to run migrations
        const migrationSuccess = await runMigrations();
        
        if (migrationSuccess) {
          console.log('Database migrations completed successfully');
          
          // Double-check that the schema is ready now
          const updatedStatus = await testSchema();
          
          if (updatedStatus.tableExists) {
            setIsInitialized(true);
          } else {
            setError('Database tables were not created successfully after migration');
          }
        } else {
          setError('Failed to run database migrations');
        }
      } else if (!schemaStatus.success) {
        setError('Failed to check database schema');
      }
    } catch (error) {
      console.error('Error initializing database:', error);
      setError((error as Error).message || 'Unknown error initializing database');
    } finally {
      setIsChecking(false);
    }
  };
  
  // Run the initialization on mount
  useEffect(() => {
    initializeDatabase();
  }, []);
  
  // The context value
  const contextValue: DatabaseContextType = {
    isInitialized,
    isChecking,
    error,
    retryInitialization: initializeDatabase
  };
  
  return (
    <DatabaseContext.Provider value={contextValue}>
      {children}
    </DatabaseContext.Provider>
  );
};
